// Imports => Vendor
import axios from 'axios';

// Imports => MOBX
import { makeObservable, observable, computed, action } from 'mobx';

// Imports => Constants
import {KEYS} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcSaveState,
  AcRemoveState,
  AcIsSet,
  AcIsNull,
  AcIsUndefined,
  AcFormatErrorMessage,
  AcFormatErrorCode,
  AcIsObject,
} from '@opp/ui-kit';
import { AcAutoSave } from '@utils';

const _default = {
  balance: null,
  profile_balance: null,
};

let app = {};

export class BalanceStore {
  constructor(store) {
    makeObservable(this);

    AcAutoSave(this);

    app.store = store;
  }

    @observable
      balance = _default.balance;

    @observable
      profile_balance = _default.profile_balance;

    @computed
    get current_balance() {
      return this.balance;
    }

    @computed
    get current_profile_balance() {
      return this.profile_balance;
    }

    @observable
      loading = {
        status: false,
        message: null,
      };

    @computed
    get is_loading() {
      return this.loading.status;
    }

    @action
      setLoading = (state, message) => {
        this.loading = {
          status: state || false,
          message,
        };
      };

    @observable
      profile_balance_loading = {
        status: false,
        message: null,
      };

    @computed
    get is_profile_balance_loading() {
      return this.profile_balance_loading.status;
    }

    @action
      setProfileBalanceLoading = (state, message) => {
        this.profile_balance_loading = {
          status: state || false,
          message,
        };
      };

    @observable
      busy = {
        status: false,
        message: null,
      };

    @computed
    get is_busy() {
      return this.busy.status;
    }

    @action
      setBusy = (state, message) => {
        this.busy = {
          status: state || false,
          message,
        };
      };

    @action
      get = (t) => {
        this.setLoading(true);

        return app.store.api.balance
          .get()
          .then(async (response) => {
            await this.set(KEYS.BALANCE, response);

            this.setLoading(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              app.store.toasters.add({
                variant: 'error',
                title: t('balance.failed-to-retrieve-balance', {ns: 'store'}),
                description: AcFormatErrorMessage(error),
                code: AcFormatErrorCode(error),
              });
            }

            this.setLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      get_profile = (t, merchant_profile_uid) => {
        this.setProfileBalanceLoading(true);

        return app.store.api.balance
          .get_profile(merchant_profile_uid)
          .then(async (response) => {
            await this.set(KEYS.PROFILE_BALANCE, response);

            this.setProfileBalanceLoading(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              app.store.toasters.add({
                variant: 'error',
                title: t('balance.failed-to-receive-profile-balance', {ns: 'store'}),
                description: AcFormatErrorMessage(error),
                code: AcFormatErrorCode(error),
              });
            }

            this.setProfileBalanceLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      withdraw = (t, data) => {
        this.setBusy(true);

        return app.store.api.balance
          .withdraw(data)
          .then(async (response) => {
            app.store.toasters.add({
              variant: 'success',
              title: t('balance.withdrawal-created', {ns: 'store'}),
            });

            this.setBusy(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              let message = t('balance.withdrawal-create-failed', {ns: 'store'});

              if (error.response && error.response.data && error.response.data.core && error.response.data.core.error && error.response.data.core.error.message) {
                message = error.response.data.core.error.message;
              }

              if (error.response && error.response.data && error.response.data.core && error.response.data.core.error && error.response.data.core.error.parameters) {
                message = Object.values(error.response.data.core.error.parameters)[0];
              }

              app.store.toasters.add({
                variant: 'error',
                title: t('balance.withdrawal-create-failed', {ns: 'store'}),
                description: message,
              });
            }
            this.setBusy(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      set = (target, value, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (AcIsUndefined(value)) return;

        return new Promise((resolve) => {
          this[target] = value;
          if (save) AcSaveState(target, value);
          resolve();
        });
      };

    @action
      setState = (target, property, value, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (!AcIsSet(property)) return;
        if (AcIsUndefined(value)) return;

        this[target][property] = value;
        if (save) AcSaveState(target, value);
      };

    @action
      reset = (target, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;

        return new Promise((resolve) => {
          this[target] = _default[target];
          if (save && AcIsNull(_default[target])) {
            AcRemoveState(target);
          } else if (save) {
            AcSaveState(target, _default[target]);
          }

          resolve();
        });
      };
}

export default BalanceStore;
